import { supportsNormalizedTemplate } from '../helpers/patterns/patternCoordinates';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  Typography,
  Upload,
  message,
} from 'antd';
import { ArrowLeftOutlined, UploadOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../helpers/api/apiConfig';
import { isMasterUser } from '../helpers/accessControl';

const { Title, Text } = Typography;

interface AreaTypeItem {
  id: number;
  name: string;
  description?: string;
  maxVertices?: number | null;
  patternCoordinateMode?: 'gps' | 'normalized' | null;
  templateDimensions?: {
    widthMeters: number;
    heightMeters: number;
  } | null;
  templateImage?: string | null;
  templateLinesImage?: string | null;
  spaceType?: 'outdoor' | 'indoor' | 'mixed' | null;
  isGlobal: boolean;
  company?: {
    id: number;
    name: string;
  } | null;
}

interface CompanyOption {
  id: number;
  name: string;
}

const AreaTypeEditor: React.FC = () => {
  const { currentUser } = useAuth();
  const isMaster = isMasterUser(currentUser);
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const areaTypeId = params.id ? Number(params.id) : null;
  const isEditMode = Boolean(areaTypeId);
  const isBackOfficeContext = location.pathname.startsWith('/back-office');
  const backTarget = isBackOfficeContext
    ? '/back-office?tab=area-types'
    : '/configuration?tab=company';

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [areaType, setAreaType] = useState<AreaTypeItem | null>(null);
  const [isGlobal, setIsGlobal] = useState(true);

  const templateImageUrl = useMemo(() => (
    isEditMode && areaType?.templateImage
      ? `${API_BASE_URL}/area-types/${areaTypeId}/template-image`
      : null
  ), [areaType?.templateImage, areaTypeId, isEditMode]);

  const templateLinesUrl = useMemo(() => (
    isEditMode && areaType?.templateLinesImage
      ? `${API_BASE_URL}/area-types/${areaTypeId}/template-lines-image`
      : null
  ), [areaType?.templateLinesImage, areaTypeId, isEditMode]);

  const buildAuthHeaders = (includeContentType = true) => {
    const token = localStorage.getItem('accessToken');
    const csrfToken = localStorage.getItem('csrfToken');
    return {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(includeContentType ? { 'Content-Type': 'application/json' } : {}),
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    };
  };

  useEffect(() => {
    if (!isMaster) return;
    const loadCompanies = async () => {
      setCompaniesLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/companies`, {
          headers: buildAuthHeaders(),
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          setCompanies((data.data || data || []).map((company: any) => ({
            id: company.id,
            name: company.name,
          })));
        }
      } catch (error) {
        console.error('Failed to load companies', error);
      } finally {
        setCompaniesLoading(false);
      }
    };

    loadCompanies();
  }, [isMaster]);

  const loadAreaType = useCallback(async () => {
    if (!areaTypeId) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/area-types/${areaTypeId}`, {
        headers: buildAuthHeaders(),
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          const record: AreaTypeItem = data.data || data;
          setAreaType(record);
          setIsGlobal(record.isGlobal);
          form.setFieldsValue({
            name: record.name,
            description: record.description || '',
            maxVertices: record.maxVertices ?? null,
            patternCoordinateMode: record.patternCoordinateMode ?? (supportsNormalizedTemplate(record) ? 'normalized' : 'gps'),
            templateWidth: record.templateDimensions?.widthMeters,
            templateHeight: record.templateDimensions?.heightMeters,
            isGlobal: record.isGlobal,
            companyId: record.company?.id,
            spaceType: record.spaceType ?? null,
          });
        } else {
          const errorData = await response.json();
          message.error(errorData?.message || 'Failed to load area type');
        }
      } catch (error) {
        console.error('Failed to load area type', error);
      message.error('Failed to load area type');
    } finally {
      setLoading(false);
    }
  }, [areaTypeId, form]);

  useEffect(() => {
    if (!isEditMode || !areaTypeId || !isMaster) return;
    loadAreaType();
  }, [areaTypeId, isEditMode, isMaster, loadAreaType]);

  useEffect(() => {
    if (isEditMode) return;
    setIsGlobal(true);
    form.setFieldsValue({
      isGlobal: true,
      patternCoordinateMode: 'gps',
      companyId: undefined,
    });
  }, [form, isEditMode]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (!values.isGlobal && !values.companyId && !isEditMode) {
        message.error('Select a company for non-global area types');
        return;
      }

      if (values.patternCoordinateMode === 'normalized' && Number(values.maxVertices) !== 4) {
        message.error('Normalized templates require Max Vertices = 4. Use GPS points for non-standard shapes.');
        return;
      }
      setSaving(true);

      const payload: any = {
        name: values.name,
        description: values.description,
        maxVertices: values.maxVertices ?? null,
        patternCoordinateMode: values.patternCoordinateMode || 'gps',
        spaceType: values.spaceType === '' ? null : (values.spaceType ?? null),
        templateDimensions: values.templateWidth && values.templateHeight
          ? { widthMeters: values.templateWidth, heightMeters: values.templateHeight }
          : null,
      };

      let response: Response;
      if (isEditMode && areaTypeId) {
        response = await fetch(`${API_BASE_URL}/area-types/${areaTypeId}`, {
          method: 'PUT',
          headers: buildAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify(payload),
        });
      } else {
        response = await fetch(`${API_BASE_URL}/area-types`, {
          method: 'POST',
          headers: buildAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            ...payload,
            isGlobal: values.isGlobal,
            companyId: values.isGlobal ? undefined : values.companyId,
          }),
        });
      }

      if (response.ok || response.status === 204) {
        if (isEditMode) {
          message.success('Area type updated');
          loadAreaType();
        } else {
          const data = await response.json();
          const newId = data?.data?.id || data?.id;
          message.success('Area type created');
          if (newId) {
            navigate(
              isBackOfficeContext
                ? `/back-office/area-types/${newId}/edit`
                : `/area-types/${newId}/edit`
            );
          } else {
            navigate(backTarget);
          }
        }
      } else {
        let errorMessage = 'Failed to save area type';
        try {
          const errorData = await response.json();
          if (Array.isArray(errorData) && errorData[0]?.constraints) {
            errorMessage = Object.values(errorData[0].constraints).join(', ');
          } else if (Array.isArray(errorData?.details)) {
            const constraintMessages = errorData.details
              .flatMap((detail: any) => Object.values(detail?.constraints || {}));
            if (constraintMessages.length > 0) {
              errorMessage = constraintMessages.join(', ');
            } else {
              errorMessage = errorData?.message || errorData?.error || errorMessage;
            }
          } else {
            errorMessage = errorData?.message || errorData?.error || errorMessage;
          }
        } catch (parseError) {
          const errorText = await response.text();
          if (errorText) {
            errorMessage = errorText;
          }
        }
        message.error(errorMessage);
      }
    } catch (error) {
      console.error('Failed to save area type', error);
      message.error('Failed to save area type');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadAsset = async (type: 'template-image' | 'template-lines-image', file: File) => {
    if (!areaTypeId) {
      message.error('Save the area type before uploading assets');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_BASE_URL}/area-types/${areaTypeId}/${type}`, {
        method: 'POST',
        headers: buildAuthHeaders(false),
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        message.success('Template uploaded');
        loadAreaType();
      } else {
        let errorMessage = 'Upload failed';
        try {
          const errorData = await response.json();
          errorMessage = errorData?.message || errorData?.error || errorMessage;
        } catch (parseError) {
          const errorText = await response.text();
          if (errorText) {
            errorMessage = errorText;
          }
        }
        message.error(errorMessage);
      }
    } catch (error) {
      console.error('Failed to upload template asset', error);
      message.error('Upload failed');
    }
  };

  if (!isMaster) {
    return (
      <Alert
        message="Access Restricted"
        description="Only master users can manage area types."
        type="warning"
        showIcon
      />
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(backTarget)}
          style={{ paddingLeft: 0 }}
        >
          {isBackOfficeContext ? 'Back to Back Office' : 'Back to Configuration'}
        </Button>
        <Title level={3} style={{ margin: 0 }}>
          {isEditMode ? 'Edit Area Type' : 'Create Area Type'}
        </Title>
        <Text type="secondary">
          Manage the full area type definition, templates, and constraints.
        </Text>
      </Space>

      <Card style={{ marginTop: 16 }} loading={loading}>
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Name"
                name="name"
                rules={[{ required: true, message: 'Name is required' }]}
              >
                <Input placeholder="e.g., Golf Green" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Max Vertices" name="maxVertices">
                <InputNumber min={1} max={9999} style={{ width: '100%' }} placeholder="Unlimited if empty" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Pattern coordinates" name="patternCoordinateMode"
            extra="Use GPS points for irregular areas or non-standard template images. Normalized coordinates are only for a standard four-corner template that can be reused across matching areas.">
            <Select options={[
              { value: 'gps', label: 'GPS points — specific to each area (image is reference only)' },
              { value: 'normalized', label: 'Normalized template — reusable points from 0 to 1 (four corners)' },
            ]} />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <Input.TextArea rows={3} placeholder="Optional description" />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Space Type" name="spaceType">
                <Select
                  placeholder="Any (compatible with all)"
                  allowClear
                  options={[
                    { label: '🌿 Outdoor', value: 'outdoor' },
                    { label: '🏢 Indoor', value: 'indoor' },
                    { label: '🔀 Mixed', value: 'mixed' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Template Width (m)" name="templateWidth">
                <InputNumber min={1} max={10000} style={{ width: '100%' }} placeholder="Optional" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Template Height (m)" name="templateHeight">
                <InputNumber min={1} max={10000} style={{ width: '100%' }} placeholder="Optional" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Global" name="isGlobal" valuePropName="checked">
                <Switch
                  checked={isGlobal}
                  onChange={(checked) => {
                    setIsGlobal(checked);
                    form.setFieldsValue({
                      isGlobal: checked,
                      companyId: checked ? undefined : form.getFieldValue('companyId'),
                    });
                  }}
                  disabled={isEditMode}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              {!isGlobal && (
                <Form.Item
                  label="Company"
                  name="companyId"
                  rules={[{ required: !isEditMode, message: 'Select a company' }]}
                >
                  <Select
                    placeholder="Select company"
                    loading={companiesLoading}
                    disabled={isEditMode}
                    options={companies.map((company) => ({
                      label: company.name,
                      value: company.id,
                    }))}
                  />
                </Form.Item>
              )}
            </Col>
          </Row>

          {!isEditMode && (
            <Alert
              message="Save to unlock templates"
              description="Template image uploads become available after you save the area type."
              type="info"
              showIcon
              style={{ marginTop: 8 }}
            />
          )}

          {isEditMode && (
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Card size="small" title="Template Image">
                  {templateImageUrl ? (
                    <img
                      src={templateImageUrl}
                      alt="Template"
                      style={{ width: '100%', borderRadius: 8, border: '1px solid #f0f0f0' }}
                    />
                  ) : (
                    <Text type="secondary">No template image uploaded.</Text>
                  )}
                  <div style={{ marginTop: 12 }}>
                    <Upload
                      accept="image/*"
                      showUploadList={false}
                      customRequest={({ file, onSuccess, onError }) => {
                        handleUploadAsset('template-image', file as File)
                          .then(() => onSuccess && onSuccess({}, new XMLHttpRequest()))
                          .catch((err) => onError && onError(err));
                      }}
                    >
                      <Button icon={<UploadOutlined />}>Replace Image</Button>
                    </Upload>
                  </div>
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card size="small" title="Template Lines">
                  {templateLinesUrl ? (
                    <div
                      style={{
                        backgroundColor: '#0f3b2f',
                        borderRadius: 8,
                        border: '1px solid #0b2a20',
                        padding: 8,
                      }}
                    >
                      <img
                        src={templateLinesUrl}
                        alt="Template lines"
                        style={{ width: '100%', display: 'block', borderRadius: 4 }}
                      />
                    </div>
                  ) : (
                    <Text type="secondary">No template lines uploaded.</Text>
                  )}
                  <div style={{ marginTop: 12 }}>
                    <Upload
                      accept="image/*"
                      showUploadList={false}
                      customRequest={({ file, onSuccess, onError }) => {
                        handleUploadAsset('template-lines-image', file as File)
                          .then(() => onSuccess && onSuccess({}, new XMLHttpRequest()))
                          .catch((err) => onError && onError(err));
                      }}
                    >
                      <Button icon={<UploadOutlined />}>Replace Lines</Button>
                    </Upload>
                  </div>
                </Card>
              </Col>
            </Row>
          )}

          <div style={{ marginTop: 16 }}>
            <Button type="primary" onClick={handleSave} loading={saving}>
              {isEditMode ? 'Save Changes' : 'Save & Continue'}
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default AreaTypeEditor;
